/* ============================================================
   INDIGREY — mobile memory guard (iOS Safari renderer crash)
   ------------------------------------------------------------
   Symptom: on iOS, opening a large collection and scrolling while it was
   still loading killed the tab with "A problem repeatedly occurred" — WebKit
   reclaiming a renderer that had gone past its memory budget. The catalogue
   was asking Safari to hold ~300 photographs, ~12 MB of image data and ~50 MB
   of decoded bitmap at once, and to do it while the adaptive-icon sampler was
   decoding a second copy of every card image onto a canvas.

   Three guards, none of which change what a shopper sees:

   1. GALLERY FRAMES. Frames 2-6 of each card ship with data-ig-src instead of
      src (see snippets/indigrey-rd-card.liquid) and are hydrated the instant a
      finger or cursor lands on the card — always before the first swipe can
      move the track, so the gallery is never caught empty.

   2. THE ADAPTIVE SAMPLER. IG.analyzeImage / IG.regionBrightness read each
      photograph on a canvas to decide whether the bag, heart and arrow icons
      should be light or dark. They are left exactly as they are visually, but
      held back until after `load`, metered one card at a time on touch
      devices, and only run for cards that actually reach the viewport —
      instead of all of them firing during the load itself.

   3. PAGE SIZE. The grid now paginates. This file turns the theme's numbered
      pagination into append-on-scroll so the experience is unchanged; the
      numbered links stay in the markup as the no-JS fallback.
   ============================================================ */
(function () {
  'use strict';

  var mq = window.matchMedia;
  var COARSE = !!(mq && (mq('(pointer: coarse)').matches || mq('(hover: none)').matches));

  /* ----------------------------------------------------------
     1. DEFERRED GALLERY FRAMES
     ---------------------------------------------------------- */
  function hydrateCard(card) {
    if (!card || card.__igFramesIn) return;
    card.__igFramesIn = true;
    var frames = card.querySelectorAll('img[data-ig-src]');
    for (var i = 0; i < frames.length; i++) {
      var img = frames[i];
      var ss = img.getAttribute('data-ig-srcset');
      if (ss) img.setAttribute('srcset', ss);
      img.setAttribute('src', img.getAttribute('data-ig-src'));
      img.removeAttribute('data-ig-src');
      img.removeAttribute('data-ig-srcset');
      img.classList.remove('ig-rd-card__img--deferred');
      img.classList.add('ig-rd-card__img--hydrated');
    }
  }
  function hydrateFrom(node) {
    var card = node && node.closest ? node.closest('.ig-rd-card') : null;
    if (card) hydrateCard(card);
  }
  /* Capture phase so this runs before the card's own handlers, and so
     pointerenter (which does not bubble) is still seen from the document. */
  ['pointerenter', 'pointerover', 'pointerdown', 'touchstart', 'focusin', 'mouseover']
    .forEach(function (type) {
      document.addEventListener(type, function (e) { hydrateFrom(e.target); }, true);
    });
  /* Belt and braces: any scroll of a card's own track hydrates it too. */
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.nodeType === 1 && t.hasAttribute && t.hasAttribute('data-ig-scroller')) hydrateFrom(t);
  }, true);

  /* ----------------------------------------------------------
     2. METERED ADAPTIVE SAMPLING
     ---------------------------------------------------------- */
  var loaded = document.readyState === 'complete';
  var GAP = COARSE ? 140 : 40;      /* ms between samples */
  var LEAD = COARSE ? '80px 0px' : '300px 0px';
  var queue = [];
  var pendingRegions = [];
  var timer = null;

  function pump() {
    if (!loaded || timer || !queue.length) return;
    timer = setTimeout(function () {
      timer = null;
      var job = queue.shift();
      if (job) job();
      pump();
    }, GAP);
  }
  function onLoaded() {
    loaded = true;
    /* let the first paint settle before any canvas work begins */
    setTimeout(function () {
      var p = pendingRegions; pendingRegions = [];
      p.forEach(function (a) { queue.push(a); });
      pump();
    }, COARSE ? 700 : 150);
  }
  if (loaded) onLoaded(); else window.addEventListener('load', onLoaded);

  function patchIG() {
    var IG = window.IG;
    if (!IG || IG.__igMetered) return;
    IG.__igMetered = true;

    var origPaint = IG.paintCardIcons;
    var origRegion = IG.regionBrightness;

    /* Cards are sampled when they reach the viewport, one at a time, rather
       than all of them 300px ahead of it during load. */
    var io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            io.unobserve(en.target);
            var card = en.target;
            queue.push(function () {
              if (!card.isConnected) return;
              card.__igPainted = true;
              origPaint.call(IG, card, card.__igPaintOpts);
            });
            pump();
          });
        }, { rootMargin: LEAD })
      : null;

    IG.paintCardIcons = function (card, opts) {
      if (!card) return;
      card.__igPaintOpts = opts;
      /* Repeat calls (a slide change) resolve from IG's own url cache, so they
         cost nothing and can go straight through. */
      if (card.__igPainted || !io) { origPaint.call(IG, card, opts); return; }
      io.observe(card);
    };

    IG.regionBrightness = function (url, rect, cb) {
      if (!url) return;
      var run = function () { origRegion.call(IG, url, rect, cb); };
      if (loaded) { queue.push(run); pump(); }
      else pendingRegions.push(run);
    };
  }
  if (window.IG) patchIG();
  else document.addEventListener('DOMContentLoaded', patchIG);

  /* ----------------------------------------------------------
     3. APPEND-ON-SCROLL PAGINATION
     ---------------------------------------------------------- */
  function gridEl()    { return document.querySelector('.ig-rd-grid'); }
  function sectionId() {
    var s = document.querySelector('.ig-rd-collection[data-section-id]');
    return s && s.getAttribute('data-section-id');
  }
  /* Shopify truncates long pagination (1 2 3 … 9), but the last page number is
     always present, so comparing the highest link to the current page is a
     reliable "is there more". */
  function nextUrl(nav) {
    if (!nav) return null;
    var cur = nav.querySelector('.pagination__item--current');
    var n = cur ? parseInt(cur.textContent.trim(), 10) : 1;
    if (isNaN(n)) n = 1;
    var max = n;
    nav.querySelectorAll('a.pagination__item').forEach(function (a) {
      var v = parseInt(a.textContent.trim(), 10);
      if (!isNaN(v) && v > max) max = v;
    });
    if (max <= n) return null;
    var u = new URL(window.location.href);
    u.searchParams.set('page', n + 1);
    return u.pathname + '?' + u.searchParams.toString();
  }

  var busy = false;
  function initMore() {
    var host = document.querySelector('.ig-rd-pagination');
    if (!host || host.__igMore) return;
    host.__igMore = true;

    var url = nextUrl(host);
    if (!url) return;
    document.documentElement.classList.add('ig-more-js');

    var wrap = document.createElement('div');
    wrap.className = 'ig-rd-more';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ig-rd-more__btn';
    btn.textContent = 'Load more';
    wrap.appendChild(btn);
    host.appendChild(wrap);

    function load() {
      if (busy || !wrap.isConnected) return;
      busy = true;
      wrap.classList.add('is-loading');
      var sid = sectionId();
      var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sid || '');
      fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('.ig-rd-grid');
          var grid = gridEl();
          var added = [];
          if (fresh && grid) {
            while (fresh.firstElementChild) {
              var node = fresh.firstElementChild;
              fresh.removeChild(node);
              grid.appendChild(node);
              if (node.classList && node.classList.contains('ig-rd-card')) added.push(node);
            }
          }
          /* swap in the fetched pagination so the next page can be found; if the
             response carried nothing, retire the control rather than loop. */
          var freshNav = doc.querySelector('.ig-rd-pagination');
          if (freshNav && added.length && host.parentNode) host.parentNode.replaceChild(freshNav, host);
          else if (host.parentNode) host.parentNode.removeChild(host);

          initCards(added);
          if (window.IG && window.IG.syncHearts) window.IG.syncHearts();
          document.dispatchEvent(new CustomEvent('ig-rd:grid-updated'));
          initMore();
        })
        .catch(function () { window.location.href = url; })
        .finally(function () { busy = false; });
    }

    btn.addEventListener('click', load);
    if ('IntersectionObserver' in window) {
      var mio = new IntersectionObserver(function (es) {
        es.forEach(function (en) {
          /* isIntersecting alone misses a fast flick that lands past the
             control — boundingClientRect.top < 0 catches "already scrolled
             past it", which is the common case with momentum scrolling. */
          if (!en.isIntersecting && en.boundingClientRect.top > 0) return;
          mio.disconnect();
          load();
        });
      }, { rootMargin: '700px 0px' });
      mio.observe(wrap);
    }
  }

  /* Cards appended by this file are outside the collection runtime's own
     initCards() pass, so the gallery controls are wired here using the same
     DOM contract and the same __igCardInit guard, so neither side double-binds. */
  function initCards(cards) {
    cards.forEach(function (card) {
      if (card.__igCardInit) return;
      card.__igCardInit = true;
      var sc = card.querySelector('[data-ig-scroller]');
      if (!sc) return;
      var n = sc.children.length;
      var l = card.querySelector('.ig-rd-card__arrow--l');
      var r = card.querySelector('.ig-rd-card__arrow--r');

      function index() { return sc.clientWidth ? Math.round(sc.scrollLeft / sc.clientWidth) : 0; }
      function show(el, on) {
        if (!el) return;
        el.classList.toggle('is-hidden', !on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
        el.tabIndex = on ? 0 : -1;
      }
      function sync() {
        var i = index();
        card.setAttribute('data-img-idx', i);
        show(l, i > 0);
        show(r, i < n - 1);
        card.querySelectorAll('.ig-rd-card__seg').forEach(function (s, k) {
          s.classList.toggle('is-on', k === i);
        });
      }
      function step(dir) {
        hydrateCard(card);
        var next = Math.min(n - 1, Math.max(0, index() + dir));
        sc.scrollTo({ left: next * sc.clientWidth, behavior: 'smooth' });
        sync();
      }
      if (l) l.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); step(-1); });
      if (r) r.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); step(1); });

      var ticking = false, settle = null;
      sc.addEventListener('scroll', function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(function () { ticking = false; sync(); });
        }
        clearTimeout(settle);
        settle = setTimeout(sync, 120);
      }, { passive: true });

      var sx = null;
      sc.addEventListener('pointerdown', function (e) { sx = e.clientX; }, { passive: true });
      sc.addEventListener('click', function (e) {
        if (sx !== null && Math.abs(e.clientX - sx) > 8) { e.preventDefault(); e.stopPropagation(); }
        sx = null;
      }, true);

      sync();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initMore);
  else initMore();
  /* the theme re-renders the grid on filter/sort — re-arm afterwards */
  document.addEventListener('ig-rd:grid-updated', function () { setTimeout(initMore, 0); });
})();
